import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { UpdateBadgeAction } from "../../Redux/Actions/RewardAction";
import { useS3Upload } from "../../hooks/useS3Upload";
import { ApiConfig } from "../../Config/CommonConfig";

interface EditBadgeModalProps {
  open: boolean;
  onClose: () => void;
  badge: any;
  onSuccess?: () => void;
}

const EditBadgeModal: React.FC<EditBadgeModalProps> = ({
  open,
  onClose,
  badge,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const {
    uploadFile,
    isLoading: s3Loading,
    s3Path,
    success: uploadSuccess,
    error: uploadError,
    resetState: resetS3State,
  } = useS3Upload();

  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    level: "",
    criteria_type: "streak",
    criteria_value: "",
    is_active: true,
    is_expire: false,
    expiry_duration: "",
  });

  useEffect(() => {
    if (badge) {
      setFormData({
        name: badge?.name || "",
        description: badge?.description || "",
        level:
          badge?.level === 1
            ? "Beginner"
            : badge?.level === 2
            ? "Intermediate"
            : badge?.level === 3
            ? "Advanced"
            : "",
        criteria_type:
          badge?.criteria_type || badge?.criteria_json?.criteria_type || "streak",
        criteria_value: badge?.criteria_json?.value?.toString() || "",
        is_active: badge?.is_active ?? true,
        is_expire: badge?.is_expire ?? false,
        expiry_duration: badge?.expiry_duration?.toString() || "",
      });
      if (badge?.image_url) {
        setImagePreview(badge?.image_url);
      }
    }
  }, [badge]);

  useEffect(() => {
    if (!open) {
      resetS3State();
      setImageFile(null);
      setImagePreview(badge?.image_url || "");
    }
  }, [open, badge, resetS3State]);

  useEffect(() => {
    if (uploadSuccess && s3Path) {
      updateBadge(s3Path);
    }
  }, [uploadSuccess, s3Path]);

  useEffect(() => {
    if (uploadError && imageFile) {
      setLoading(false);
    }
  }, [uploadError, imageFile]);

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files && e?.target?.files[0]) {
      const file = e?.target?.files[0];
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (imageFile) {
        uploadFile(ApiConfig?.BadgeUploadPresigned, imageFile, imageFile?.type);
      } else {
        updateBadge(badge?.image_url);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const updateBadge = async (imageUrl: string) => {
    try {
      let levelNumber = null;
      if (formData?.level?.toLowerCase() === "beginner") levelNumber = 1;
      if (formData?.level?.toLowerCase() === "intermediate") levelNumber = 2;
      if (formData?.level?.toLowerCase() === "advanced") levelNumber = 3;

      const badgeData: any = {};

      if (formData?.name !== badge?.name) {
        badgeData.name = formData?.name;
      }
      if (formData?.description !== badge?.description) {
        badgeData.description = formData?.description;
      }
      if (levelNumber !== badge?.level) {
        badgeData.level = levelNumber;
      }
      if (imageUrl !== badge?.image_url) {
        badgeData.image_url = imageUrl;
      }
      if (formData?.is_active !== badge?.is_active) {
        badgeData.is_active = formData?.is_active;
      }
      if (formData?.is_expire !== badge?.is_expire) {
        badgeData.is_expire = formData?.is_expire;
      }
      const currentCriteriaType =
        badge?.criteria_type || badge?.criteria_json?.criteria_type;
      const currentCriteriaValue = badge?.criteria_json?.value;
      const newCriteriaValue = Number(formData?.criteria_value);

      if (
        formData?.criteria_type !== currentCriteriaType ||
        newCriteriaValue !== currentCriteriaValue
      ) {
        badgeData.criteria_json = {
          criteria_type: formData?.criteria_type,
          value: newCriteriaValue,
        };
      }
      if (
        formData?.expiry_duration &&
        formData?.expiry_duration !== badge?.expiry_duration?.toString()
      ) {
        badgeData.expiry_duration = Number(formData?.expiry_duration);
      }
      const result = await dispatch(UpdateBadgeAction(badge?.id, badgeData));
      
      if (result && result.status === true) {
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess(); 
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setImageFile(null);
    setImagePreview(badge?.image_url || "");
    setLoading(false);
    resetS3State();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getImageFilename = (url: string) => {
    if (!url) return "";
    const urlWithoutParams = url?.split("?")[0];
    const parts = urlWithoutParams?.split("/");
    const filename = parts[parts?.length - 1];

    return decodeURIComponent(filename);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Badge: {badge?.code}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            name="name"
            label="Badge Name"
            value={formData?.name}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            name="description"
            label="Description"
            value={formData?.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
          />

          <FormControl fullWidth>
            <InputLabel>Level</InputLabel>
            <Select
              name="level"
              value={formData?.level}
              onChange={handleChange}
              label="Level"
            >
              <MenuItem value="Beginner">Beginner</MenuItem>
              <MenuItem value="Intermediate">Intermediate</MenuItem>
              <MenuItem value="Advanced">Advanced</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Criteria Type</InputLabel>
            <Select
              name="criteria_type"
              value={formData?.criteria_type}
              onChange={handleChange}
              label="Criteria Type"
            >
              <MenuItem value="streak">Streak</MenuItem>
              <MenuItem value="course">Course</MenuItem>
              <MenuItem value="assessment">Assessment</MenuItem>
            </Select>
          </FormControl>

          <TextField
            name="criteria_value"
            label="Criteria Value"
            value={formData?.criteria_value}
            onChange={handleChange}
            fullWidth
            type="number"
            helperText="Number of streaks/courses/assessments required"
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData?.is_active}
                  onChange={handleChange}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  name="is_expire"
                  checked={formData?.is_expire}
                  onChange={handleChange}
                />
              }
              label="Has Expiry"
            />
          </Box>

          {formData?.is_expire && (
            <TextField
              name="expiry_duration"
              label="Expiry Duration (days)"
              value={formData?.expiry_duration}
              onChange={handleChange}
              fullWidth
              type="number"
              helperText="Number of days until badge expires"
            />
          )}

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Update Badge Image (optional)
            </Typography>

            {imagePreview && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <img
                  src={imagePreview}
                  alt="Badge preview"
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Current Image:
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {getImageFilename(imagePreview)}
                  </Typography>
                </Box>
              </Box>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ width: "100%" }}
            />

            {imageFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "#0555c8" }}
              >
                New file selected: {imageFile?.name}
              </Typography>
            )}

            {uploadError && imageFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "error.main" }}
              >
                Upload Error: {uploadError}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || s3Loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || s3Loading || !formData?.name}
          sx={{
            backgroundColor: "#0555c8",
            "&:hover": {
              backgroundColor: "#043c99",
            },
          }}
        >
          {loading || s3Loading ? <CircularProgress size={24} /> : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBadgeModal;
